import { motion } from "framer-motion";

const brands = [
  {
    name: "Begum's Biryani Secrets",
    tagline: "Royal Mughlai Heritage. Modern Cloud Delivery.",
    description:
      "An imperial biryani brand built on slow-cooked dum traditions, sealed packaging and an aroma-driven unboxing ritual. Designed for premium positioning at scale.",
    accent: "#8C2C3F",
    accentSoft: "rgba(140, 44, 63, 0.1)",
    cuisine: "Mughlai · Biryani · Kebabs",
    image:
      "https://images.unsplash.com/photo-1589302168068-964664d93dc0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    badge: "Heritage",
    pillars: ["Slow-cooked Dum", "Sealed Packaging", "Premium Positioning"],
  },
  {
    name: "Rollit",
    tagline: "Modern Urban Rolls. Fast Casual.",
    description:
      "An urban, high-velocity rolls brand engineered for Gen-Z palates and 12-minute delivery windows. A youthful, energetic identity built for the fast-casual generation.",
    accent: "#C4682B",
    accentSoft: "rgba(196, 104, 43, 0.12)",
    cuisine: "Rolls · Fast Casual · Quick Bites",
    image:
      "https://images.unsplash.com/photo-1626700051175-6818013e1d4f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    badge: "Fast Casual",
    pillars: ["Quick Service", "Youthful Identity", "Urban Format"],
  },
  {
    name: "Let's Chaap",
    tagline: "Smoke. Spice. Soyabean Soul.",
    description:
      "A premium North Indian chaap and grill concept — sizzling tandoor formats, smoky char and plant-based plates that travel beautifully across every delivery radius.",
    accent: "#A23F2A",
    accentSoft: "rgba(162, 63, 42, 0.12)",
    cuisine: "Chaap · Tandoor · Grills",
    image:
      "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    badge: "Tandoor",
    pillars: ["Sizzling Tandoor", "Plant-based", "Travel-ready Plates"],
  },
];

export default function SceneBrands() {
  return (
    <section
      id="brands"
      data-testid="scene-brands"
      className="relative w-full bg-beige"
    >
      <div className="section">
        <div className="container-k">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.9 }}
            className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-20"
          >
            <div>
              <div className="flex items-center gap-3 mb-6">
                <span className="font-mono-label text-[var(--k-gold)]">Chapter 03</span>
                <div className="w-12 h-px bg-[var(--k-olive)]" />
              </div>
              <h2
                className="font-serif-display text-5xl md:text-7xl text-[var(--k-text)] leading-[1]"
                data-testid="brands-headline"
              >
                Three brands. <br />
                <em className="green-gradient-text not-italic">One profit machine.</em>
              </h2>
            </div>
            <p className="text-[var(--k-text-2)] text-base max-w-md leading-relaxed">
              Each KOSTRA brand is engineered for delivery — distinct
              positioning, shared infrastructure. Operate one. Operate all
              three from a single kitchen.
            </p>
          </motion.div>

          <div className="flex flex-col gap-6">
            {brands.map((b, i) => (
              <BrandCard key={b.name} brand={b} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function BrandCard({ brand, index }) {
  const reverse = index % 2 === 1;
  return (
    <motion.article
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`brand-card-${index}`}
      className="relative grid grid-cols-1 lg:grid-cols-12 overflow-hidden organic-card !p-0"
      style={{ minHeight: "520px" }}
    >
      <div className={`lg:col-span-7 relative overflow-hidden ${reverse ? "lg:order-2" : ""}`}>
        <motion.img
          src={brand.image}
          alt={brand.name}
          className="w-full h-full object-cover absolute inset-0"
          initial={{ scale: 1.1 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.6 }}
        />
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(${reverse ? "270deg" : "90deg"}, ${brand.accentSoft}, transparent 40%, rgba(255,255,255,0.05))`,
          }}
        />
        <div className="absolute top-6 left-6 flex items-center gap-3">
          <span
            className="font-mono-label text-[0.6rem] px-3 py-1.5 border rounded-full bg-white/80 backdrop-blur"
            style={{ borderColor: brand.accent, color: brand.accent }}
          >
            {brand.badge}
          </span>
        </div>
        <div
          className="absolute bottom-6 right-6 font-serif-display text-7xl leading-none"
          style={{ color: "rgba(255,255,255,0.4)" }}
        >
          0{index + 1}
        </div>
      </div>

      <div
        className={`lg:col-span-5 p-10 md:p-14 flex flex-col justify-center relative bg-[var(--k-card)] ${
          reverse ? "lg:order-1" : ""
        }`}
      >
        <div
          className="absolute top-0 left-0 w-full h-px"
          style={{ background: `linear-gradient(90deg, ${brand.accent}, transparent)` }}
        />
        <span className="font-mono-label text-[var(--k-muted)] mb-4">{brand.cuisine}</span>
        <h3
          className="font-serif-display text-4xl md:text-5xl text-[var(--k-text)] leading-[1] mb-5"
          data-testid={`brand-name-${index}`}
        >
          {brand.name}
        </h3>
        <p
          className="font-serif-display italic text-xl mb-7"
          style={{ color: brand.accent }}
        >
          {brand.tagline}
        </p>
        <p className="text-[var(--k-text-2)] leading-relaxed text-sm">
          {brand.description}
        </p>

        <ul className="mt-8 pt-7 border-t border-[var(--k-line)] flex flex-wrap gap-x-6 gap-y-3">
          {brand.pillars.map((p) => (
            <li
              key={p}
              className="flex items-center gap-2 font-mono-label text-[var(--k-text-2)] text-[0.6rem]"
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: brand.accent }}
              />
              {p}
            </li>
          ))}
        </ul>
      </div>
    </motion.article>
  );
}
